import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Alert from '@amzn/awsui-components-console/alert';
import Spinner from '@amzn/awsui-components-console/spinner';
import TextContent from '@amzn/awsui-components-console/text-content';
import HTMLEditor from '../HTMLEditor/HTMLEditor';
import './EmbeddableEditor.scss';
import { ALLOWED_IFRAME_ORIGINS } from '../../../../constants/endpoints';
import { artifactService } from '../../../../services/artifactService';
import { getStageFromUrl } from '../../../../utils/stageUtil';

interface MessageData {
  type: 'DOWNLOAD' | 'LOAD_HTML' | 'GET_CONTENT' | 'SET_MODE' | 'CLEAR_CONTENT' | 'SAVE_CONTENT';
  payload?: {
    html?: string;
    isEditMode?: boolean;
  };
  messageId?: string;
}

const EmbeddableEditor: React.FC = () => {
  const { artifactId } = useParams<{ artifactId: string }>();
  const [searchParams] = useSearchParams();

  // Determine initial edit mode from query parameters
  // Supports: ?mode=edit, ?mode=view
  const getInitialEditMode = (): boolean => {
    const modeParam = searchParams.get('mode');

    if (modeParam) {
      return modeParam.toLowerCase() === 'edit';
    }

    return false; // Default to view mode
  };

  const [htmlContent, setHtmlContent] = useState<string>('');
  const [lastSavedContent, setLastSavedContent] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(getInitialEditMode());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error' | 'edited'>('idle');
  const [artifactMetadata, setArtifactMetadata] = useState<{ name: string; version: number } | null>(null);

  // Get allowed origins based on current stage
  const currentStage = getStageFromUrl();
  const allowedOrigins = ALLOWED_IFRAME_ORIGINS[currentStage] || [];

  // Load artifact if artifactId is present
  useEffect(() => {
    if (artifactId) {
      loadArtifact(artifactId);
    }
  }, [artifactId]);

  const loadArtifact = async (id: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await artifactService.fetchArtifact(id);

      if (!data.artifact) {
        throw new Error('Artifact not found');
      }

      if (data.artifact.artifactS3Path) {
        const html = await artifactService.fetchHTMLContent(data.artifact.artifactS3Path);
        setHtmlContent(html);
        setLastSavedContent(html); // Track the last saved state
        setSaveStatus('idle'); // Reset save status when loading

        // Store artifact metadata for saving
        setArtifactMetadata({
          name: data.artifact.artifactName || 'Untitled',
          version: data.artifact.latestVersion || 1,
        });

        // Notify parent window that content has been loaded
        window.parent.postMessage(
          {
            type: 'ARTIFACT_LOADED',
            payload: {
              artifactId: id,
              html: html,
              artifactData: data,
            },
          },
          '*',
        );
      } else {
        throw new Error('Artifact does not have an S3 path');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load artifact';
      setError(errorMessage);

      // Notify parent window about the error
      window.parent.postMessage(
        {
          type: 'ARTIFACT_LOAD_ERROR',
          payload: {
            artifactId: id,
            error: errorMessage,
          },
        },
        '*',
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (allowedOrigins[0] !== '*' && !allowedOrigins.includes(event.origin)) {
        console.warn('Message from unauthorized origin:', event.origin);
        return;
      }

      const message = event.data as MessageData;
      console.log('received message in editor:', message);

      switch (message.type) {
        case 'SET_MODE': {
          const newMode = message.payload?.isEditMode ?? false;
          setIsEditMode(newMode);
          event.source?.postMessage(
            {
              type: 'MODE_CHANGED',
              payload: { isEditMode: newMode },
              messageId: message.messageId,
            },
            { targetOrigin: event.origin },
          );
          break;
        }

        case 'DOWNLOAD': {
          // Check if the content is already a complete HTML document
          const isCompleteHTML =
            htmlContent.trim().toLowerCase().startsWith('<!doctype html') || htmlContent.trim().toLowerCase().startsWith('<html');

          const fullHTML = isCompleteHTML
            ? htmlContent
            : `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Edited HTML</title>
</head>
<body>
${htmlContent}
</body>
</html>`;

          const blob = new Blob([fullHTML], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'edited-content.html';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          event.source?.postMessage(
            {
              type: 'DOWNLOAD_INITIATED',
              messageId: message.messageId,
            },
            { targetOrigin: event.origin },
          );
          break;
        }

        case 'LOAD_HTML':
          if (message.payload?.html) {
            setHtmlContent(message.payload.html);
            // Don't update lastSavedContent here as this is just loading new content, not saving
            event.source?.postMessage(
              {
                type: 'HTML_LOADED',
                messageId: message.messageId,
              },
              { targetOrigin: event.origin },
            );
          }
          break;

        case 'GET_CONTENT':
          event.source?.postMessage(
            {
              type: 'CONTENT_RESPONSE',
              payload: { html: htmlContent },
              messageId: message.messageId,
            },
            { targetOrigin: event.origin },
          );
          break;

        case 'CLEAR_CONTENT':
          setHtmlContent('');
          // Check if we need to update save status
          if (lastSavedContent !== '') {
            setSaveStatus('edited');
          }
          event.source?.postMessage(
            {
              type: 'CONTENT_CLEARED',
              messageId: message.messageId,
            },
            { targetOrigin: event.origin },
          );
          break;

        case 'SAVE_CONTENT': {
          // Check if content has actually changed
          if (htmlContent === lastSavedContent) {
            // Content hasn't changed, just show success without calling API
            setSaveStatus('success');
            // Keep success status displayed until content changes

            event.source?.postMessage(
              {
                type: 'SAVE_SUCCESS',
                payload: { artifactId, message: 'No changes to save' },
                messageId: message.messageId,
              },
              { targetOrigin: event.origin },
            );
            break;
          }

          // Save the current content if an artifact ID is available
          if (artifactId && artifactMetadata) {
            setSaveStatus('saving');
            setSaveError(null);

            artifactService
              .saveArtifact(artifactId, htmlContent, artifactMetadata.name, artifactMetadata.version + 1)
              .then(() => {
                // Update version after successful save
                setArtifactMetadata((prev) => (prev ? { ...prev, version: prev.version + 1 } : null));
                setLastSavedContent(htmlContent); // Update last saved content
                setSaveError(null); // Clear any previous save errors
                setSaveStatus('success');
                // Keep success status displayed until content changes

                event.source?.postMessage(
                  {
                    type: 'SAVE_SUCCESS',
                    payload: { artifactId },
                    messageId: message.messageId,
                  },
                  { targetOrigin: event.origin },
                );
              })
              .catch((error) => {
                const errorMessage = error instanceof Error ? error.message : 'Failed to save';
                setSaveError(errorMessage);
                setSaveStatus('error');
                // Keep error status displayed until content changes or user tries to save again

                event.source?.postMessage(
                  {
                    type: 'SAVE_ERROR',
                    payload: {
                      artifactId,
                      error: errorMessage,
                    },
                    messageId: message.messageId,
                  },
                  { targetOrigin: event.origin },
                );
              });
          } else {
            const errorMessage = 'No artifact ID or metadata available for saving';
            setSaveError(errorMessage);
            setSaveStatus('error');
            // Keep error status displayed until content changes or user tries to save again

            event.source?.postMessage(
              {
                type: 'SAVE_ERROR',
                payload: { error: errorMessage },
                messageId: message.messageId,
              },
              { targetOrigin: event.origin },
            );
          }
          break;
        }

        default:
          console.warn('Unknown message type:', message.type);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      console.log('removed event listener');
    };
  }, [htmlContent, isEditMode, allowedOrigins, artifactId, artifactMetadata, lastSavedContent, saveStatus, saveError]);

  const handleContentChange = (content: string) => {
    setHtmlContent(content);
    // Update save status based on whether content differs from last saved
    if (content !== lastSavedContent) {
      // Content has been modified
      if (saveStatus === 'success' || saveStatus === 'idle') {
        setSaveStatus('edited');
      } else if (saveStatus === 'error') {
        // Keep error status but track that content changed
        setSaveStatus('edited');
      }
    } else {
      // Content matches last saved
      if (saveStatus === 'edited') {
        // If it was edited and now matches saved, show success
        setSaveStatus('success');
      } else if (saveStatus === 'error') {
        // Clear error if content reverted to last saved
        setSaveStatus('success');
      }
    }
    window.parent.postMessage(
      {
        type: 'CONTENT_CHANGED',
        payload: { html: content },
      },
      '*',
    );
  };

  React.useEffect(() => {
    // Remove default body margins for full-page display
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflow = '';
    };
  }, []);

  if (isLoading) {
    return (
      <div className="embeddable-editor__loading-container">
        <div className="loading-content">
          <Spinner size="large" />
          <TextContent>
            <p>Loading artifact...</p>
          </TextContent>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="embeddable-editor__error-container">
        <div className="error-content">
          <Alert type="error" header="Your artifact could not be loaded">
            {error}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="embeddable-editor">
      {saveError && (
        <div style={{ padding: '16px' }}>
          <Alert dismissible onDismiss={() => setSaveError(null)} type="error" header="Your content could not be saved">
            {saveError}
          </Alert>
        </div>
      )}
      <HTMLEditor initialContent={htmlContent} isEditMode={isEditMode} onContentChange={handleContentChange} saveStatus={saveStatus} />
    </div>
  );
};

export default EmbeddableEditor;
